import {
  createContext,
  FC,
  memo,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import useAsyncEffect from 'use-async-effect';
import EventsService from '@services/EventsService';
import ParticipantsService from '@services/ParticipantsService';

import {
  IMessage,
  MessageType,
  EAudioPlaybackType,
} from '@core/assistant/interfaces';
import {
  assistantSize,
  bedrockResultNotFoundReply,
  debugEventId,
  debugTagName,
} from '@core/assistant/constants';
import { SCREENS, AWAITING_RESPONSE_TYPE } from '@core/assistant/interfaces';

import { IAssistantContext, ProviderProps } from './interfaces';
import { visualizeAudio } from './AudioVisualizer';
import { shared } from './Shared';
import { log, initDebugSendFn } from './Debug';

import {
  LinearScriptPlayer,
  ScriptNode,
} from './scriptPlayer/linearScriptPlayer';
import { ScriptNodeJSON } from './scriptPlayer/intefraces';
import AssistantScriptsService from '@services/AssistantScriptService';
import { IAssistantScript } from '@interfaces/assistantScript';
import AssistantQueryService from '@services/AssistantQueryService';
import { regionNameToCamelCase, formatSpotRegionName } from './Regions';
import {
  IEvent,
  IEventSettingsAiAssistant,
  PollyLanguage,
} from '@interfaces/event';
import { teleportToRegion as teleportToRegionFn } from './providerV1';

export const AssistantContext = createContext<IAssistantContext>(
  {} as IAssistantContext,
);

export const useAssistant = () => useContext(AssistantContext);

let visualizerInstance: any;
// TODO: temp workaround, do a proper fix on SM side to have an option to use Transcibe only
let activeScreenGlob = SCREENS.DEFAULT;
// TODO: temp workaround
let isMutedGlob = false;
let currentRegion = '';

const defaultAiSettings: IEventSettingsAiAssistant = {
  engine: 'generative',
  voiceId: 'Ruth',
  language: PollyLanguage.ENGLISH,
};

const getCurrentRegion = () => {
  let currentArea = currentRegion;

  if (!currentRegion && debugTagName) {
    currentArea = debugTagName;
  } else if (!currentRegion && !debugTagName) {
    currentArea = 'landing';
  }

  return currentArea;
};

const AssistantProviderInner: FC<ProviderProps> = ({
  children,
  currentRegionName,
  userName,
  mytaverseEventRef,
  sendWSRef,
}) => {
  const [open, setOpen] = useState(false);
  const [isAudioPlaybackActive, setIsAudioPlaybackActive] = useState(false);
  const [audioEndTick, setAudioEndTick] = useState(0);
  const [isScriptCompleted, setIsScriptCompleted] = useState(false);
  const [awaitingResponseType, setAwaitingResponseType] = useState(
    AWAITING_RESPONSE_TYPE.NONE,
  );
  const [activeScreen, setActiveScreen] = useState<SCREENS>(SCREENS.DEFAULT);
  const [navigationHistory, setNavigationHistory] = useState<SCREENS[]>([
    SCREENS.DEFAULT,
  ]);
  const [scripts, setScripts] = useState<IAssistantScript[]>([]);

  const [audioPromptHistory] = useState<string[]>([]);

  const [isRecording, setIsRecording] = useState(false);
  const [isMuted, setIsMutedLoc] = useState(false);
  const [isDisplayUserFeedback, setIsDisplayUserFeedback] = useState(false);
  const [isScriptQuizActive, setIsScriptQuizActive] = useState(false);
  const [lastUserMessageData, setLastUserMessageData] = useState({
    eventId: '',
    participantId: '',
    timestamp: 0,
  });

  const [chatHistory, setChatHistory] = useState<IMessage[]>([]);

  const progressState = useRef('{}');
  const saveProgressQueueRef = useRef<Promise<void>>(Promise.resolve());

  // refs come from props — no direct hook calls to useMytaverse/useMytaverseEvent here
  const sendJSONMessageToWebSocketRef = sendWSRef;

  const navigationHistoryRef = useRef(navigationHistory);
  navigationHistoryRef.current = navigationHistory;

  const openRef = useRef(open);
  openRef.current = open;

  const scriptsRef = useRef(scripts);
  scriptsRef.current = scripts;

  const userNameRef = useRef(userName);
  userNameRef.current = userName;

  const linearScriptPlayerRef = useRef<LinearScriptPlayer>();
  const isAudioPlaybackActiveRef = useRef<boolean>(false);
  const audioPlaybackIdRef = useRef<number>(0);
  const debugEventAiSettingsRef = useRef<IEventSettingsAiAssistant | null>(
    null,
  );

  const getEffectiveAiSettings = (): {
    engine: string;
    voiceId: string;
    language: string;
  } => {
    const mytaverseEvent = mytaverseEventRef.current;
    let eventAiSettings: IEventSettingsAiAssistant | undefined;

    if (mytaverseEvent?.currentEventId) {
      // Try currentEvent first, fall back to searching the events list
      eventAiSettings =
        mytaverseEvent.currentEvent?.settings?.aiAssistant ??
        mytaverseEvent.events?.find(
          (event: any) => event.eventId === mytaverseEvent.currentEventId,
        )?.settings?.aiAssistant;
    } else if (debugEventId) {
      eventAiSettings = debugEventAiSettingsRef.current ?? undefined;
    }

    return {
      engine: eventAiSettings?.engine || defaultAiSettings.engine,
      voiceId: eventAiSettings?.voiceId || defaultAiSettings.voiceId,
      language: eventAiSettings?.language || defaultAiSettings.language,
    };
  };

  const preRenderedAudioRef = useRef<
    {
      text: string;
      audio: null | ArrayBuffer;
      voiceId: string;
      engine: string;
      language: string;
      area: string;
      index: number;
    }[]
  >([]);

  const setIsMuted = useCallback(() => {
    setIsMutedLoc((prev) => {
      isMutedGlob = !prev;
      return !prev;
    });
  }, []);

  const navigate = useCallback((screen: SCREENS, navigateBack?: boolean) => {
    if (navigateBack) {
      const history = navigationHistoryRef.current;
      activeScreenGlob = history[history.length - 2];
      setActiveScreen(history[history.length - 2]);
    } else {
      activeScreenGlob = screen;
      setActiveScreen(screen);
    }

    setNavigationHistory((prevState) => {
      if (navigateBack) {
        prevState.pop();
        return [...prevState];
      }

      return [...prevState, screen];
    });
  }, []);

  const addMessage = useCallback(
    ({ text, type }: { text: string; type: MessageType }) => {
      setChatHistory((prevState) => {
        return [...prevState, { text, type, timestamp: Date.now() }];
      });
    },
    [],
  );

  const handleSendRequest = useCallback(
    async (text: string) => {
      addMessage({ text, type: MessageType.USER });
      setIsDisplayUserFeedback(false);
      setAwaitingResponseType(AWAITING_RESPONSE_TYPE.SHORT);

      shared.audioPlayBackType = EAudioPlaybackType.CHAT;
      log('currentRegion', currentRegion);

      const currentArea = getCurrentRegion();

      log('useAudioZoneBasedContext currentRegion', currentArea);
      log(mytaverseEventRef.current?.currentEventId);

      const modelResponse = await AssistantQueryService.queryAssistant({
        text,
        tag: formatSpotRegionName(currentArea),
        eventId: mytaverseEventRef.current?.currentEventId || 'debug',
        includeScriptContext:
          mytaverseEventRef.current?.currentEventId || debugTagName
            ? true
            : false,
      });

      setAwaitingResponseType(AWAITING_RESPONSE_TYPE.NONE);

      if (modelResponse.text !== bedrockResultNotFoundReply) {
        generatePollyAudio(modelResponse.text);
      }

      addMessage({ text: modelResponse.text, type: MessageType.ASSISTANT });

      if (
        modelResponse.text === bedrockResultNotFoundReply &&
        modelResponse.lastUserMessageData
      ) {
        setIsDisplayUserFeedback(true);
        setLastUserMessageData(modelResponse.lastUserMessageData);
      }

      log('useAudioZoneBasedContext modelResponse', modelResponse);
    },
    [addMessage],
  );

  const handleSubmitUserFeedback = useCallback(
    async (isUserApproved: boolean) => {
      await AssistantQueryService.flagAssistantReply({
        eventId: lastUserMessageData.eventId,
        timestamp: lastUserMessageData.timestamp,
        isFeedbackRequired: isUserApproved ? 2 : 3,
      });
      setIsDisplayUserFeedback(false);

      if (isUserApproved) {
        addMessage({
          text: 'Your request is recorded',
          type: MessageType.ASSISTANT,
        });
      }
    },
    [lastUserMessageData, addMessage],
  );

  const isAIEnabledForEvent = useCallback(() => {
    const eventRef = mytaverseEventRef.current;
    const settings = eventRef.events?.find(
      (event: IEvent) => event.eventId === eventRef.currentEventId,
    )?.settings;

    if (
      eventRef?.currentEventId &&
      settings?.aiEnabled &&
      settings?.aiVirtualAssistantName === 'Noa'
    ) {
      return true;
    }

    return false;
  }, []);

  function audioEndCallback(playbackId: number) {
    log('[audio] audioEndCallback fired', {
      playbackId,
      current: audioPlaybackIdRef.current,
    });

    if (playbackId !== audioPlaybackIdRef.current) {
      log(
        '[audio] audioEndCallback STALE — ignoring (stopped manually before natural end)',
      );
      return;
    }
    log('[audio] audioEndCallback NATURAL END');
    isAudioPlaybackActiveRef.current = false;
    linearScriptPlayerRef.current?.notifyAudioEnded();

    const linearScriptPlayerState =
      linearScriptPlayerRef.current?.getCurrentState();

    if (
      linearScriptPlayerState?.isRunning &&
      (!linearScriptPlayerState?.currentNode.advanceOnAudioEnd ||
        linearScriptPlayerState?.currentNode.awaitButtonNext)
    ) {
      setIsAudioPlaybackActive(false);
      setAudioEndTick((t) => t + 1);
    }

    if (
      linearScriptPlayerState?.isRunning &&
      linearScriptPlayerState?.currentNode.advanceOnAudioEnd &&
      !linearScriptPlayerState?.currentNode.awaitButtonNext
    ) {
      log('auto advance script');
      linearScriptPlayerRef.current?.triggerNext();
      log(
        'script current state',
        linearScriptPlayerRef.current?.getCurrentState(),
      );
    }

    log(
      'current node',
      linearScriptPlayerRef.current?.getCurrentState().currentNode.id,
    );
  }

  const stopPlayback = useCallback(() => {
    log('[audio] stopPlayback', {
      nextPlaybackId: audioPlaybackIdRef.current + 1,
    });
    log('[assistant] stopPlayback caller');
    audioPlaybackIdRef.current++;
    isAudioPlaybackActiveRef.current = false;
    setIsAudioPlaybackActive(false);

    try {
      visualizerInstance.stopAudio();
    } catch (e) {}
  }, []);

  const teleportToRegion = useCallback(
    (regionName: string) =>
      teleportToRegionFn(regionName, sendJSONMessageToWebSocketRef),
    [],
  );

  function onGenerateAudio(data: any) {
    log('activeScreen', activeScreenGlob);
    log('isMutedGlob', isMutedGlob);

    if (/*activeScreenGlob !== SCREENS.DEFAULT &&*/ !isMutedGlob) {
      log('onGenerateAudio data len', data.length);
      isAudioPlaybackActiveRef.current = true;
      log('isAudioPlaybackActiveRef', isAudioPlaybackActiveRef);
      setIsAudioPlaybackActive(true);
      const capturedPlaybackId = audioPlaybackIdRef.current;

      // important, check if audio is playing before stopping to avoid audio glitches
      if (visualizerInstance && visualizerInstance.playing) {
        log(
          '[audio] onGenerateAudio stopping previous visualizer before starting new one',
        );
        visualizerInstance.stopAudio();
      }

      log('[audio] onGenerateAudio START', { playbackId: capturedPlaybackId });
      visualizerInstance = visualizeAudio(
        assistantSize.width,
        assistantSize.height,
        () => audioEndCallback(capturedPlaybackId),
      );
      visualizerInstance.initialize(data);
    }
  }

  const generatePollyAudio = async (
    text: string,
    engine?: string,
    voiceId?: string,
    language?: string,
    disableOptimizations: boolean = true,
  ) => {
    log('generatePollyAudio muted', isMutedGlob);
    if (isMutedGlob) {
      isAudioPlaybackActiveRef.current = false;
      setIsAudioPlaybackActive(false);
      setAudioEndTick((t) => t + 1);
      linearScriptPlayerRef.current?.notifyAudioEnded();

      const mutedPlayerState = linearScriptPlayerRef.current?.getCurrentState();

      if (
        mutedPlayerState?.isRunning &&
        mutedPlayerState?.currentNode.advanceOnAudioEnd &&
        !mutedPlayerState?.currentNode.awaitButtonNext
      ) {
        linearScriptPlayerRef.current?.triggerNext();
      }

      return;
    }

    if (isAudioPlaybackActiveRef.current && visualizerInstance) {
      log('[audio] generatePollyAudio stopping previous audio for new request');
      visualizerInstance.stopAudio();
    }
    isAudioPlaybackActiveRef.current = false;

    const existingAudio = preRenderedAudioRef.current.find(
      (x) => x.text === text,
    );

    log('existingAudio', existingAudio);

    if (existingAudio?.audio) {
      log('prerendered audio exists', existingAudio);
      log(
        `Polly audio size before playback: ${(
          existingAudio.audio.byteLength / 1024
        ).toFixed(2)} KB`,
      );
      onGenerateAudio(existingAudio.audio);
      return;
    }

    let arrayBuffer: ArrayBuffer | null = null;

    try {
      arrayBuffer = await AssistantQueryService.generateAssistantAudio({
        text,
        disableOptimizations,
        engine,
        voiceId,
        language,
      });
    } catch (e) {
      log('Error: failed to fetch Polly audio', e);
      return;
    }

    if (!arrayBuffer || arrayBuffer.byteLength === 0) {
      log('Error: empty or missing Polly audio response');
      return;
    }

    log(
      `Polly audio size before playback: ${(
        arrayBuffer.byteLength / 1024
      ).toFixed(2)} KB`,
    );

    if (existingAudio) {
      existingAudio.audio = arrayBuffer;
    }

    onGenerateAudio(arrayBuffer);
  };

  const playBlockAudio = useCallback(
    async (
      text: string,
      engine?: string,
      voiceId?: string,
      language?: string,
    ) => {
      log('playback block text ->', text);

      if (!openRef.current) {
        setOpen(true);

        setTimeout(() => {
          generatePollyAudio(text, engine, voiceId, language, false);
        }, 100);
        return;
      }
      generatePollyAudio(text, engine, voiceId, language, false);
    },
    [],
  );

  // Serialize all server saves through a queue so concurrent calls can't
  // arrive out of order and overwrite each other. Each enqueued save always
  // reads progressState.current at execution time, so it always sends the
  // latest accumulated state.
  const enqueueProgressSave = useCallback(() => {
    const stateAtEnqueue = progressState.current;
    log(
      '[progress] enqueueProgressSave called, state at enqueue:',
      stateAtEnqueue,
    );
    saveProgressQueueRef.current = saveProgressQueueRef.current.then(
      async () => {
        log(
          '[progress] queue item executing, state at execute time:',
          progressState.current,
        );
        if (mytaverseEventRef.current?.currentEventId || debugEventId) {
          await EventsService.setEventUserData(
            mytaverseEventRef.current?.currentEventId ?? debugEventId,
            { assistantState: progressState.current },
          );
          log('[progress] server save done, saved:', progressState.current);
        } else {
          log('[progress] SKIPPED server save — no eventId');
        }
      },
    );
  }, []);

  const cbOnCompletedStep = useCallback(
    (trigger: string, stepNumber: number, step: string) => {
      log(
        `[progress] cbOnCompletedStep called — trigger=${trigger} stepNumber=${stepNumber} step=${step} state BEFORE:`,
        progressState.current,
      );
      log(`${trigger} step ${step} completed`, {
        progressState: progressState.current,
        trigger,
      });

      const currentProgressState: {
        [key: string]: Array<[number, number, number[]?]>;
      } = JSON.parse(progressState.current || '{}');

      if (!currentProgressState[trigger]) {
        currentProgressState[trigger] = [];
      }
      // skip saving progress if entry already exists to avoid duplicates
      const progressEntryAlreadyExists = currentProgressState[trigger].some(
        (e) => e[0] === stepNumber,
      );
      if (!progressEntryAlreadyExists) {
        currentProgressState[trigger].push([stepNumber, Date.now()]);
      }

      progressState.current = JSON.stringify(currentProgressState);
      log(
        `[progress] cbOnCompletedStep state AFTER write:`,
        progressState.current,
      );

      log(
        'assistant progress saved',
        JSON.stringify(currentProgressState, null, 2),
      );

      enqueueProgressSave();
    },
    [enqueueProgressSave],
  );

  const saveQuizAnswers = useCallback(
    async (selectedOptions: number[]) => {
      const player = linearScriptPlayerRef.current;
      const trigger = player?.getCurrentState().name;
      const nodeId = player?.getCurrentNodeId();

      log('saveQuizAnswers input', {
        trigger,
        nodeId,
        progressState: progressState.current,
      });

      if (!trigger || !nodeId) {
        log('saveQuizAnswers: no trigger or nodeId');
        return;
      }

      const stepNumber = parseInt(nodeId.replace('step-', ''), 10) - 1;
      if (isNaN(stepNumber)) {
        log('saveQuizAnswers: invalid stepNumber from nodeId', nodeId);
        return;
      }

      const currentProgressState: {
        [key: string]: Array<[number, number, number[]?]>;
      } = JSON.parse(progressState.current || '{}');

      if (!currentProgressState[trigger]) {
        currentProgressState[trigger] = [];
      }
      const entries = currentProgressState[trigger];

      const entryIndex = entries.findIndex((e) => e[0] === stepNumber);

      if (entryIndex !== -1) {
        // cbOnCompletedStep ran first and entry already exists, add collected answers
        entries[entryIndex][2] = selectedOptions;
      } else {
        // cbOnCompletedStep hasn't run yet, create entry now with collected answers
        entries.push([stepNumber, Date.now(), selectedOptions]);
      }
      progressState.current = JSON.stringify(currentProgressState);

      log('quiz answers saved', JSON.stringify(currentProgressState, null, 2));

      enqueueProgressSave();
    },
    [enqueueProgressSave],
  );

  const startFlow = useCallback((scriptTrigger: string, scriptId?: string) => {
    setIsScriptCompleted(false);

    const currentScripts = scriptsRef.current;
    const script = scriptId
      ? currentScripts.find((x) => x.scriptId === scriptId)
      : currentScripts.find(
          (x) =>
            x.scriptTrigger === scriptTrigger ||
            x.scriptTrigger.includes(scriptTrigger),
        );
    const scriptData = script?.scriptData;

    let parsedScriptData: ScriptNodeJSON[] = [];

    if (scriptData) {
      try {
        parsedScriptData = JSON.parse(scriptData);
        log('whole script', JSON.stringify(parsedScriptData, null, 2));
      } catch (error) {
        log('makePlayerFromScriptData error', error);
      }
    }

    if (!parsedScriptData.length) {
      return;
    }

    const currentArea =
      scriptTrigger && !scriptId ? scriptTrigger : getCurrentRegion();

    const playerState = linearScriptPlayerRef.current?.getCurrentState();
    if (playerState?.isRunning && playerState?.name === currentArea) {
      log('script player is already running');
      return;
    }

    const currentProgressState = JSON.parse(progressState.current || '{}');

    log('currentProgressState player', {
      currentProgressState,
      currentAreaProgress:
        currentProgressState?.[currentArea]?.at(-1)?.[0] ?? 0,
      currentAreaTotalSteps: parsedScriptData.length,
    });

    if (
      parsedScriptData.length > 0 &&
      currentProgressState?.[currentArea] &&
      (currentProgressState?.[currentArea]?.at(-1)?.[0] ?? 0) >=
        parsedScriptData.length - 1
    ) {
      log('script is already completed');
      return;
    }

    const effectiveAiSettings = getEffectiveAiSettings();

    const nodes: ScriptNode[] = parsedScriptData.map((node, index) => {
      let advanceOnAudioEndFallback = false;
      // inject user name
      node.text = node.text.replace(
        '{{UserName}}',
        userNameRef.current ? ', ' + userNameRef.current : '',
      );

      // inject experience name
      node.text = node.text.replace(
        '{{ExperienceName}}',
        mytaverseEventRef.current.currentEvent?.name
          ? ', ' + mytaverseEventRef.current.currentEvent?.name
          : '',
      );

      // force script completion on last step if no advance trigger is specified
      if (
        index === parsedScriptData.length - 1 &&
        !node.awaitButtonNext &&
        node.questionary?.type !== 'multiple_choice' &&
        !node.advanceOnAudioEnd &&
        !node.allowedKeys?.length &&
        !node.enableClickAnywhere
      ) {
        advanceOnAudioEndFallback = true;
      }

      if (!preRenderedAudioRef.current.find((x) => x.text === node.text)) {
        preRenderedAudioRef.current.push({
          area: currentArea,
          index: index + 1,
          audio: null,
          text: node.text,
          engine: effectiveAiSettings.engine,
          voiceId: effectiveAiSettings.voiceId,
          language: effectiveAiSettings.language,
        });
      }

      return {
        id: `step-${index + 1}`,
        play: () => {
          cbOnCompletedStep(currentArea, index, `step-${index + 1}`);
          return playBlockAudio(
            node.text,
            effectiveAiSettings.engine,
            effectiveAiSettings.voiceId,
            effectiveAiSettings.language,
          );
        },
        awaitButtonNext:
          node.questionary?.type === 'multiple_choice'
            ? true
            : node.awaitButtonNext ?? false,
        awaitAudioEndBeforeInput: node.allowedKeys?.length ? true : false,
        advanceOnAudioEnd: advanceOnAudioEndFallback
          ? advanceOnAudioEndFallback
          : node.advanceOnAudioEnd,
        delayBeforeAdvancingMs: node.delayBeforeAdvancingMs ?? 0,
        enableClickAnywhere: node.enableClickAnywhere ?? false,
        allowedKeys: node.allowedKeys || [],
        cbOnCompletedStep: () => {},
        questionary: node.questionary,
        text: node.text,
        //cbOnCompletedStep(currentArea, index + 1, `step-${index + 1}`),
      };
    });

    nodes.push({
      id: `step-${nodes.length + 1}`,
      play: async () => {
        linearScriptPlayerRef.current?.complete();
        setIsScriptCompleted(true);

        /*cbOnCompletedStep(
          currentArea,
          parsedScriptData.length,
          `step-${parsedScriptData.length}`,
        );*/
      },
    });

    log('build nodes', nodes);

    log('preRenderedAudio ' + currentArea, preRenderedAudioRef.current);

    if (
      !currentProgressState[currentArea] ||
      (currentProgressState?.[currentArea] &&
        (currentProgressState?.[currentArea]?.at(-1)?.[0] ?? 0) <
          nodes.length - 2)
    ) {
      setIsScriptCompleted(false);
      linearScriptPlayerRef.current = new LinearScriptPlayer(nodes, {
        name: currentArea,
        onComplete: () => setIsScriptCompleted(true),
      });
      let step = 1;

      if (currentProgressState?.[currentArea]) {
        step = (currentProgressState?.[currentArea]?.at(-1)?.[0] ?? -1) + 2;
      }

      log(
        `[progress] startFlow starting player at step-${step}, progressState:`,
        progressState.current,
      );
      linearScriptPlayerRef.current.start(`step-${step}`);

      startAudioPrerender(currentArea, step);

      //log('polly response', pollyResponse);
    }
  }, []);

  const startAudioPrerender = async (
    currentArea: string,
    startStep: number,
  ) => {
    const preRenderedAudioScript = preRenderedAudioRef.current.filter(
      (x) => x.area === currentArea && !x.audio,
    );
    log(
      'startAudioPrerender audio ' + currentArea,
      preRenderedAudioScript.length,
    );

    for (let i = startStep; i < preRenderedAudioScript.length; i++) {
      log('startAudioPrerender audio ' + i);

      try {
        const arrayBuffer = await AssistantQueryService.generateAssistantAudio({
          text: preRenderedAudioScript[i].text,
          disableOptimizations: true,
          engine: preRenderedAudioScript[i].engine,
          voiceId: preRenderedAudioScript[i].voiceId,
          language: preRenderedAudioScript[i].language,
        });

        if (!arrayBuffer || arrayBuffer.byteLength === 0) {
          log('Error: empty or missing Polly audio response for step ' + i);
          continue;
        }

        preRenderedAudioScript[i].audio = arrayBuffer;
        log(
          'startAudioPrerender audio ' + i,
          `${(arrayBuffer.byteLength / 1024).toFixed(2)} KB`,
        );
      } catch (e) {
        log('Error: failed to fetch Polly audio for step ' + i, e);
      }
    }

    log('preRenderedAudioScript', preRenderedAudioScript);
  };

  const clearPrerenderedAudio = useCallback(() => {
    preRenderedAudioRef.current = [];
    log('prerendered audio cleared');
  }, []);

  const fetchScripts = useCallback(async () => {
    const scripts = await AssistantScriptsService.getAssistantScripts();

    log('loaded assistant scripts', scripts);

    setScripts(scripts);

    if (!mytaverseEventRef.current?.currentEventId && debugEventId) {
      try {
        const eventParticipantData = await EventsService.getEvent(debugEventId);
        const aiAssistantSettings =
          eventParticipantData?.event?.settings?.aiAssistant;

        if (aiAssistantSettings) {
          debugEventAiSettingsRef.current = aiAssistantSettings;
          log('loaded debug event AI assistant settings', aiAssistantSettings);
        }
      } catch (error) {
        log('failed to load debug event AI assistant settings', error);
      }
    }
  }, []);

  const mergeProgressStates = (
    current: string | null | undefined,
    stored: string | null | undefined,
  ): string => {
    const currentState: Record<
      string,
      Array<[number, number, number[]?]>
    > = JSON.parse(current || '{}');
    const storedState: Record<
      string,
      Array<[number, number, number[]?]>
    > = JSON.parse(stored || '{}');

    const merged = { ...storedState };
    for (const [key, entries] of Object.entries(currentState)) {
      if (!merged[key]) {
        merged[key] = entries;
      } else {
        // keep all entries from current that db doesn't have
        for (const entry of entries) {
          const exists = merged[key].some((e) => e[0] === entry[0]);
          if (!exists) {
            merged[key].push(entry);
          }
        }
        // sort by stepNumber ascending
        merged[key].sort((a, b) => a[0] - b[0]);
      }
    }
    return JSON.stringify(merged);
  };

  const fetchParticipantProgress = useCallback(async () => {
    log(
      '[progress] fetchParticipantProgress called, current state BEFORE:',
      progressState.current,
    );
    if (!mytaverseEventRef.current?.currentEventId) {
      // debug only
      const allParticipantEvents =
        await ParticipantsService.getCurrentParticipantEvents();
      const eventParticipantData = allParticipantEvents.events.find(
        (event) => event.eventId === debugEventId,
      );

      if (eventParticipantData) {
        log(
          '[progress] fetchParticipantProgress server assistantState:',
          eventParticipantData?.assistantState,
        );
        progressState.current = mergeProgressStates(
          progressState.current,
          eventParticipantData?.assistantState as string,
        );
      } else {
        log('no debug state data');
      }
    } else {
      log(
        '[progress] fetchParticipantProgress server assistantState:',
        mytaverseEventRef.current.currentEventParticipant?.assistantState,
      );
      progressState.current = mergeProgressStates(
        progressState.current,
        mytaverseEventRef.current.currentEventParticipant
          ?.assistantState as string,
      );
    }

    log(
      '[progress] fetchParticipantProgress state AFTER merge:',
      progressState.current,
    );
    log('loaded participant progress', progressState.current);
  }, []);

  const resetProgress = useCallback(async () => {
    log('[progress] resetProgress called');
    // Clear in-memory state immediately so any writes that happen while the
    // HTTP request is in-flight start from a clean slate.
    progressState.current = '{}';
    // Make the reset save the new queue base so that any saves enqueued
    // after this are guaranteed to arrive at the server AFTER the reset.
    const resetSave = EventsService.setEventUserData(
      mytaverseEventRef.current?.currentEventId ?? debugEventId,
      { assistantState: null },
    ).catch(() => {});
    saveProgressQueueRef.current = resetSave;
    await resetSave;
    log('[progress] resetProgress done, state:', progressState.current);
  }, []);

  useEffect(() => {
    initDebugSendFn((msg) => sendJSONMessageToWebSocketRef.current(msg));
    return () => initDebugSendFn(() => {});
  }, []);

  if (!mytaverseEventRef.current) {
    mytaverseEventRef.current = {} as any;
  }

  useEffect(() => {
    // Guard: transient null regions (e.g. from camera-sharing WS messages)
    // should not stop playback or reset the script player.
    if (!currentRegionName) return;

    // Use the prop directly, not the live ref — the ref may be null or stale from a camera-sharing WS message
    // that arrived between the render updating the prop and the effect running.
    const incomingRegion = currentRegionName;

    log('region has changed to', incomingRegion);

    // If the script player is already running for this exact region AND audio
    // is actively playing, don't stop and restart — that causes double playback
    // on UE5 reconnect. Require isAudioPlaybackActiveRef so a player stuck in
    // isRunning=true with no audio (e.g. after AudioContext closes) gets reset.
    const playerState = linearScriptPlayerRef.current?.getCurrentState();
    if (
      playerState?.isRunning &&
      playerState.name === incomingRegion &&
      isAudioPlaybackActiveRef.current
    ) {
      log('region script already running with active audio — skipping restart');
      return;
    }

    const scriptData = scriptsRef.current.find((x) => {
      const scriptTrigger = (
        Array.isArray(x.scriptTrigger) ? x.scriptTrigger : [x.scriptTrigger]
      ).map((trigger) =>
        trigger.includes('_') ? regionNameToCamelCase(trigger) : trigger,
      );

      if (scriptTrigger.includes(incomingRegion as string)) {
        return true;
      }
    });

    // If the new region's script is already complete but a different region's audio
    // is playing, the event is likely spurious (e.g. Zoom SDK side-effect) — skip
    // processing to avoid interrupting playback. Fall through if nothing is playing
    // or it's the same region, so a stale "completed" flag doesn't block audio.
    if (scriptData) {
      const parsedNodes: ScriptNodeJSON[] = JSON.parse(
        scriptData.scriptData || '[]',
      );
      const progress = JSON.parse(progressState.current || '{}');
      const isCompleted =
        parsedNodes.length > 0 &&
        (progress[incomingRegion]?.at(-1)?.[0] ?? 0) >= parsedNodes.length - 1;
      if (isCompleted) {
        const currentPlayerState =
          linearScriptPlayerRef.current?.getCurrentState();
        const protectingDifferentRegion =
          (currentPlayerState?.isRunning || isAudioPlaybackActiveRef.current) &&
          currentPlayerState?.name !== incomingRegion;
        if (protectingDifferentRegion) {
          log(
            'region script already completed — updating region silently, not stopping audio',
            incomingRegion,
          );
          currentRegion = incomingRegion;
          return;
        }
        // Not protecting a different region — fall through to normal flow.
      }
    }

    if (linearScriptPlayerRef.current?.getCurrentState().isRunning) {
      linearScriptPlayerRef.current?.stop();
    }

    stopPlayback();

    if (incomingRegion) {
      currentRegion = incomingRegion;
    }

    if (scriptData) {
      startFlow('', scriptData.scriptId);
    }
  }, [currentRegionName]);

  useAsyncEffect(async () => {
    await Promise.all([fetchScripts(), fetchParticipantProgress()]);
  }, []);

  useEffect(() => {
    log('current assistant progress state', progressState);
  }, [progressState]);

  const contextValue = useMemo(
    () => ({
      activeScreen,
      setActiveScreen,
      open,
      setOpen,
      handleSendRequest,
      chatHistory,
      navigate,
      setNavigationHistory,
      awaitingResponseType,
      stopPlayback,
      isAIEnabledForEvent,
      isRecording,
      setIsRecording,
      audioPromptHistory,
      isMuted,
      setIsMuted,
      teleportToRegion,
      startFlow,
      linearScriptPlayerRef,
      isAudioPlaybackActiveRef,
      isAudioPlaybackActive,
      scripts,
      isDisplayUserFeedback,
      setIsDisplayUserFeedback,
      handleSubmitUserFeedback,
      fetchScripts,
      fetchParticipantProgress,
      resetProgress,
      saveQuizAnswers,
      clearPrerenderedAudio,
      isScriptQuizActive,
      setIsScriptQuizActive,
      audioEndTick,
      isScriptCompleted,
      setIsScriptCompleted,
      userName: userName || '',
    }),
    [
      activeScreen,
      open,
      handleSendRequest,
      chatHistory,
      navigate,
      awaitingResponseType,
      stopPlayback,
      isAIEnabledForEvent,
      isRecording,
      audioPromptHistory,
      isMuted,
      setIsMuted,
      teleportToRegion,
      startFlow,
      isAudioPlaybackActive,
      scripts,
      isDisplayUserFeedback,
      handleSubmitUserFeedback,
      fetchScripts,
      fetchParticipantProgress,
      resetProgress,
      saveQuizAnswers,
      clearPrerenderedAudio,
      isScriptQuizActive,
      audioEndTick,
      isScriptCompleted,
      userName,
    ],
  );

  return (
    <AssistantContext.Provider value={contextValue}>
      {children}
    </AssistantContext.Provider>
  );
};

export const AssistantProvider = memo(AssistantProviderInner);
